import type {
  ApiError,
  DebugLogLevel,
  DebugLogListResponse,
  DebugLogStats,
  GameplayPreset,
  GameplayCatalogEntry,
  FieldErrorDetail,
  CombatAbilityData,
  CombatAbilityInput,
  CombatAbilityListResponse,
  CombatAbilityOption,
  CombatKitData,
  CombatKitInput,
  CombatKitListResponse,
  ItemBatchImportResult,
  ItemBatchPreview,
  ItemData,
  ItemBulkRequest,
  ItemBulkResult,
  ItemListPreference,
  ItemListResponse,
  ReferenceData,
  UserSummary,
} from '../shared/types';


function csrfToken() {
  const row = document.cookie.split('; ').find((value) => value.startsWith('myy_csrf='));
  return row ? decodeURIComponent(row.split('=').slice(1).join('=')) : '';
}

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string | null,
    public readonly requestId: string | null,
    public readonly details: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  get fieldErrors(): FieldErrorDetail[] {
    if (!this.details || typeof this.details !== 'object' || !('fieldErrors' in this.details)) return [];
    const value = (this.details as { fieldErrors?: unknown }).fieldErrors;
    return Array.isArray(value) ? value.filter((entry): entry is FieldErrorDetail => Boolean(entry && typeof entry === 'object' && 'field' in entry && 'message' in entry)) : [];
  }

  fieldMessage(field: string): string | null {
    const exact=this.fieldErrors.find((entry)=>entry.field===field);
    const nested=this.fieldErrors.find((entry)=>entry.field.startsWith(`${field}.`));
    return exact?.message ?? nested?.message ?? null;
  }
}

let reportingClientError = false;

async function sendClientLog(level: DebugLogLevel, event: string, message: string, details?: unknown): Promise<void> {
  if (reportingClientError) return;
  reportingClientError = true;
  try {
    const headers = new Headers({ 'content-type': 'application/json' });
    const csrf = csrfToken();
    if (csrf) headers.set('x-csrf-token', csrf);
    await fetch('/api/v1/logs/client', {
      method: 'POST',
      credentials: 'same-origin',
      headers,
      body: JSON.stringify({ level, event, message, details, route: location.pathname }),
    });
  } catch {
    // Logging must never break the UI.
  } finally {
    reportingClientError = false;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData) && !headers.has('content-type')) headers.set('content-type', 'application/json');
  if (options.method && !['GET', 'HEAD'].includes(options.method.toUpperCase())) {
    const csrf = csrfToken();
    if (csrf) headers.set('x-csrf-token', csrf);
  }

  let response: Response;
  try { response = await fetch(path, { ...options, headers, credentials: 'same-origin' }); }
  catch (error) {
    void sendClientLog('error', 'client.network_error', `Netzwerkfehler bei ${path}.`, { path, method: options.method ?? 'GET', error: String(error) });
    throw new ApiRequestError(`Die Verbindung zum Content Studio ist fehlgeschlagen (${options.method ?? 'GET'} ${path}).`, 0, 'NETWORK_ERROR', null, error);
  }

  const responseRequestId = response.headers.get('x-request-id');
  if (!response.ok) {
    let body: ApiError | null = null;
    try { body = await response.json() as ApiError; } catch {}
    const code = body?.error?.code ?? null;
    const requestId = body?.error?.requestId ?? responseRequestId;
    const baseMessage = body?.error?.message ?? `HTTP ${response.status}`;
    const reference = [code ? `Fehlercode: ${code}` : null, requestId ? `Vorgang: ${requestId}` : null].filter(Boolean).join(' · ');
    throw new ApiRequestError(reference ? `${baseMessage} (${reference})` : baseMessage, response.status, code, requestId, body?.error?.details ?? null);
  }

  if (response.status === 204) return undefined as T;
  try { return await response.json() as T; }
  catch (error) {
    throw new ApiRequestError(`Der Server hat eine ungültige Antwort geliefert${responseRequestId ? ` (Vorgang: ${responseRequestId})` : ''}.`, response.status, 'INVALID_RESPONSE', responseRequestId, error);
  }
}

export function debugLogQuery(filters: { level?: string; from?: string; to?: string; search?: string; limit?: number; offset?: number }) {
  const params = new URLSearchParams();
  if (filters.level) params.set('level', filters.level);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.search) params.set('search', filters.search);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.offset !== undefined) params.set('offset', String(filters.offset));
  return params.toString();
}

let referenceCache: ReferenceData | null = null;
let referenceInFlight: Promise<ReferenceData> | null = null;
function invalidateReferenceCache() { referenceCache = null; referenceInFlight = null; }
async function cachedReference(): Promise<ReferenceData> {
  if (referenceCache) return referenceCache;
  if (!referenceInFlight) referenceInFlight=request<ReferenceData>('/api/v1/reference-data').then((value)=>{referenceCache=value;return value;}).finally(()=>{referenceInFlight=null;});
  return referenceInFlight;
}


async function uploadOptimizedIcon<T>(entityPath: 'items'|'abilities'|'kits', id: string, file: File, thumbnail: File): Promise<T> {
  const headers = new Headers();
  const csrf = csrfToken();
  if (csrf) headers.set('x-csrf-token', csrf);
  const form = new FormData();
  form.set('icon', file, file.name);
  form.set('thumbnail', thumbnail, thumbnail.name);
  const response = await fetch(`/api/v1/files/${entityPath}/${encodeURIComponent(id)}/icon`, { method:'PUT', body:form, headers, credentials:'same-origin' });
  if (!response.ok) {
    let body: ApiError | null = null;
    try { body = await response.json() as ApiError; } catch {}
    const requestId = body?.error.requestId ?? response.headers.get('x-request-id');
    const message = body?.error.message ?? 'Bild konnte nicht hochgeladen werden.';
    throw new ApiRequestError(`${message}${requestId ? ` (Vorgang: ${requestId})` : ''}`, response.status, body?.error.code ?? null, requestId, body?.error.details ?? null);
  }
  return response.json() as Promise<T>;
}

export const api = {
  login: (username: string, password: string) => request<{ user: UserSummary }>('/api/v1/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  logout: () => request<{ ok: boolean }>('/api/v1/auth/logout', { method: 'POST' }),
  me: () => request<{ user: UserSummary }>('/api/v1/auth/me'),
  reference: () => cachedReference(),
  domains: () => request<{ domains: GameplayCatalogEntry[] }>('/api/v1/reference-data/domains'),
  updateDomain: async (domain: GameplayCatalogEntry) => {
    const result=await request<{ domain: GameplayCatalogEntry; affectedContent: number }>(`/api/v1/reference-data/domains/${encodeURIComponent(domain.technicalKey)}`, { method:'PUT', body:JSON.stringify({displayName:domain.displayName,description:domain.description,damageNumberColorHex:domain.damageNumberColorHex,sortOrder:domain.sortOrder,active:domain.active}) });
    invalidateReferenceCache();
    return result;
  },
  gameplayPresets: () => request<{ presets: GameplayPreset[] }>('/api/v1/reference-data/gameplay-presets'),
  updateGameplayPreset: async (preset: GameplayPreset) => {
    const result=await request<{ preset: GameplayPreset; affectedContent: number }>(`/api/v1/reference-data/gameplay-presets/${preset.type}/${encodeURIComponent(preset.technicalKey)}`, { method: 'PUT', body: JSON.stringify(preset) });
    invalidateReferenceCache();
    return result;
  },
  abilities: () => request<CombatAbilityListResponse>('/api/v1/abilities'),
  abilityOptions: () => request<{ abilities: CombatAbilityOption[] }>('/api/v1/abilities/options'),
  ability: (id: string) => request<{ ability: CombatAbilityData }>(`/api/v1/abilities/${encodeURIComponent(id)}`),
  createAbility: (data: CombatAbilityInput) => request<{ ability: CombatAbilityData }>('/api/v1/abilities', { method: 'POST', body: JSON.stringify(data) }),
  updateAbility: (id: string, data: CombatAbilityInput) => request<{ ability: CombatAbilityData }>(`/api/v1/abilities/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) }),
  archiveAbility: (id: string) => request<{ ability: CombatAbilityData }>(`/api/v1/abilities/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  kits: () => request<CombatKitListResponse>('/api/v1/kits'),
  kit: (id: string) => request<{ kit: CombatKitData }>(`/api/v1/kits/${encodeURIComponent(id)}`),
  createKit: (data: CombatKitInput) => request<{ kit: CombatKitData }>('/api/v1/kits', { method: 'POST', body: JSON.stringify(data) }),
  updateKit: (id: string, data: CombatKitInput) => request<{ kit: CombatKitData }>(`/api/v1/kits/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) }),
  items: (query = '') => request<ItemListResponse>(`/api/v1/items${query.startsWith('?') ? query : `?${query}`}`),
  itemListPreference: () => request<ItemListPreference>('/api/v1/preferences/item-list'),
  saveItemListPreference: (preference: ItemListPreference) => request<ItemListPreference>('/api/v1/preferences/item-list', { method: 'PUT', body: JSON.stringify(preference) }),
  checkTechnicalKey: (technicalKey: string) => request<{ technicalKey: string; available: boolean; suggestedKey: string; requestId?: string }>(`/api/v1/items/check-key?technicalKey=${encodeURIComponent(technicalKey)}`),
  item: (id: string) => request<{ item: ItemData }>(`/api/v1/items/${encodeURIComponent(id)}`),
  createItem: (data: Omit<ItemData, 'id'|'icon'|'revision'|'unrealSync'|'createdAt'|'updatedAt'> & { duplicateSourceId?: string }) => request<{ item: ItemData }>('/api/v1/items', { method: 'POST', body: JSON.stringify(data) }),
  updateItem: (id: string, data: Omit<ItemData, 'id'|'icon'|'revision'|'unrealSync'|'createdAt'|'updatedAt'>) => request<{ item: ItemData }>(`/api/v1/items/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) }),
  archiveItem: (id: string) => request<{ item: ItemData }>(`/api/v1/items/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  restoreItem: (id: string) => request<{ item: ItemData }>(`/api/v1/items/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
  bulkUpdateItems: (data: ItemBulkRequest) => request<ItemBulkResult>('/api/v1/items/bulk', { method: 'POST', body: JSON.stringify(data) }),
  requestItemDeletion: (id: string) => request<{ item: ItemData }>(`/api/v1/items/${encodeURIComponent(id)}/delete-request`, { method: 'POST' }),
  finalizeItemDeletion: (id: string) => request<{ ok: boolean; itemId: string; technicalKey: string; failedObjectCleanup: number }>(`/api/v1/items/${encodeURIComponent(id)}/finalize`, { method: 'DELETE' }),
  uploadIcon: (id: string, file: File, thumbnail: File) => uploadOptimizedIcon<{ item: ItemData }>('items',id,file,thumbnail),
  uploadAbilityIcon: (id: string, file: File, thumbnail: File) => uploadOptimizedIcon<{ ability: CombatAbilityData }>('abilities',id,file,thumbnail),
  uploadKitIcon: (id: string, file: File, thumbnail: File) => uploadOptimizedIcon<{ kit: CombatKitData }>('kits',id,file,thumbnail),

  itemBatchExampleUrl: '/api/v1/imports/items/example.csv',
  previewItemBatch: (csv: string) => request<ItemBatchPreview>('/api/v1/imports/items/preview', { method: 'POST', headers: { 'content-type': 'text/csv; charset=utf-8' }, body: csv }),
  commitItemBatch: (csv: string) => request<ItemBatchImportResult>('/api/v1/imports/items/commit', { method: 'POST', headers: { 'content-type': 'text/csv; charset=utf-8' }, body: csv }),
  logs: (filters: Parameters<typeof debugLogQuery>[0]) => request<DebugLogListResponse>(`/api/v1/logs?${debugLogQuery(filters)}`),
  logStats: (filters: Parameters<typeof debugLogQuery>[0]) => request<DebugLogStats>(`/api/v1/logs/stats?${debugLogQuery(filters)}`),
  logCsvUrl: (filters: Parameters<typeof debugLogQuery>[0]) => `/api/v1/logs/export.csv?${debugLogQuery(filters)}`,
  clearLogs: () => request<{ ok: boolean; deletedCount: number }>('/api/v1/logs', { method: 'DELETE' }),
  logClient: (level: DebugLogLevel, event: string, message: string, details?: unknown) => sendClientLog(level, event, message, details),
};
